// Layout-ът на `/inventory/:page`: менюто (InventorySidebar), страницата по
// slug и работните листове на собствени routes. Кои страници има и в коя
// секция са — `components/shell/inventoryNav.ts`; тук е само slug → страница.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../hooks/useViewport";
import { useSidebarCollapse } from "../../hooks/useSidebarCollapse";
import { useWarehouses, useInventorySettings } from "../../hooks/useInventory";
import { getRestaurantName } from "../../lib/restaurant";
import { InventorySidebar } from "./components/sidebar/InventorySidebar";
import {
  DEFAULT_INVENTORY_SLUG,
  INVENTORY_NAV_SLUGS,
} from "./components/shell/inventoryNav";
import { useSidebarBadges } from "./components/shell/useSidebarBadges";
import { textDragGuardProps } from "./components/textDragGuard";
import {
  InventoryShellContext,
  type InventoryShell,
  type LeaveGuard,
} from "./components/shell/InventoryShellContext";
import { DeliveryHomePage } from "./delivery/DeliveryHomePage";
import { DeliveriesReport } from "./reports/deliveries/DeliveriesReport";
import { DeliveryPaymentsPage } from "./payments/DeliveryPaymentsPage";
import { DeliveryWorksheetPage } from "./delivery/DeliveryWorksheetPage";
import { WriteoffHomePage } from "./writeoff/WriteoffHomePage";
import { WriteoffsReport } from "./reports/writeoffs/WriteoffsReport";
import { WriteoffCreatePage } from "./writeoff/WriteoffCreatePage";
import { WriteoffEditPage } from "./writeoff/WriteoffEditPage";
import { TransferHomePage } from "./transfer/TransferHomePage";
import { TransfersReport } from "./reports/transfers/TransfersReport";
import { TransferWorksheetPage } from "./transfer/TransferWorksheetPage";
import { StocktakeHomePage } from "./stocktake/StocktakeHomePage";
import { StocktakesReport } from "./reports/stocktakes/StocktakesReport";
import { StocktakeWorksheetPage } from "./stocktake/StocktakeWorksheetPage";
import { StockReport } from "./reports/StockReport";
import { ShortagePage } from "./shortage/ShortagePage";
import { SubstitutionsReport } from "./reports/SubstitutionsReport";
import { DeletedReport } from "./reports/DeletedReport";
import { SoldReport } from "./reports/SoldReport";
import { InterchangeablesPage } from "./interchangeables/InterchangeablesPage";
import { InterchangeableRulePage } from "./interchangeables/InterchangeableRulePage";
import { ReportPostingPage } from "./posting/ReportPostingPage";
import { PostedReportsReport } from "./reports/postedReports/PostedReportsReport";
import { SettingsPage } from "./settings/SettingsPage";
import { InventoryDashboardPage } from "./dashboard/InventoryDashboardPage";

// Общите props, които всяка складова страница получава. Новите страници четат
// същото от useInventoryShell().
type CommonPageProps = {
  onOpenMobileMenu: () => void;
  isNarrow: boolean;
  warehouseId: number | null;
  warehouseName: string | null;
  onRegisterLeaveGuard: (guard: LeaveGuard | null) => void;
};

type InvPage = {
  titleKey: string;
  render: (p: CommonPageProps) => ReactNode;
};

// slug → страница. Всеки slug от менюто ТРЯБВА да е тук (проверява се долу).
const PAGES: Record<string, InvPage> = {
  // ─── Табло (началната страница) ───
  dashboard: {
    titleKey: "inventoryPage.dashNav",
    render: () => <InventoryDashboardPage />,
  },
  // ─── Операции ───
  posting: {
    titleKey: "inventoryPage.posting",
    render: () => <ReportPostingPage />,
  },
  delivery: {
    titleKey: "inventoryPage.delivery",
    render: () => <DeliveryHomePage />,
  },
  "delivery-payments": {
    titleKey: "inventoryPage.navDeliveryPayments",
    render: () => <DeliveryPaymentsPage />,
  },
  writeoff: {
    titleKey: "inventoryPage.writeoff",
    render: (p) => <WriteoffHomePage {...p} />,
  },
  shortage: {
    titleKey: "inventoryPage.negativeReport",
    render: (p) => <ShortagePage {...p} />,
  },
  stocktake: {
    titleKey: "inventoryPage.stocktake",
    render: (p) => <StocktakeHomePage {...p} />,
  },
  interchangeables: {
    titleKey: "inventoryPage.interchange",
    render: (p) => <InterchangeablesPage {...p} />,
  },
  transfer: {
    titleKey: "inventoryPage.transfer",
    render: (p) => <TransferHomePage {...p} />,
  },
  // ─── Справки ───
  "posted-reports": {
    titleKey: "inventoryPage.navPostedReports",
    render: () => <PostedReportsReport />,
  },
  deliveries: {
    titleKey: "inventoryPage.navDeliveriesReport",
    render: () => <DeliveriesReport />,
  },
  writeoffs: {
    titleKey: "inventoryPage.navWriteoffsReport",
    render: (p) => <WriteoffsReport {...p} />,
  },
  stocktakes: {
    titleKey: "inventoryPage.navStocktakesReport",
    render: (p) => <StocktakesReport {...p} />,
  },
  transfers: {
    titleKey: "inventoryPage.navTransfersReport",
    render: (p) => <TransfersReport {...p} />,
  },
  "sold-products": {
    titleKey: "inventoryPage.soldProducts",
    render: (p) => <SoldReport {...p} kind="product" />,
  },
  "sold-recipes": {
    titleKey: "inventoryPage.soldRecipes",
    render: (p) => <SoldReport {...p} kind="recipe" />,
  },
  stock: {
    titleKey: "inventoryPage.stockReport",
    render: (p) => <StockReport {...p} />,
  },
  substitutions: {
    titleKey: "inventoryPage.substitutionsReport",
    render: (p) => <SubstitutionsReport {...p} />,
  },
  deleted: {
    titleKey: "inventoryPage.deletedReport",
    render: (p) => <DeletedReport {...p} />,
  },
  // ─── Настройки ───
  settings: {
    titleKey: "inventoryPage.settings",
    render: (p) => <SettingsPage {...p} />,
  },
};

if (import.meta.env.DEV) {
  const missing = INVENTORY_NAV_SLUGS.filter((slug) => !PAGES[slug]);
  if (missing.length) console.error("Inventory: менюто няма страница за", missing);
}

// worksheet: работен лист (създаване/редакция) — цяла страница на собствен
// route (/inventory/delivery/new, /inventory/writeoff/:id/edit, …), вътре в
// layout-а (менюто остава). page определя към коя страница принадлежи (кой
// ред в менюто свети и къде води „назад").
type InventoryWorksheet = {
  page:
    | "delivery"
    | "writeoff"
    | "stocktake"
    | "interchangeables"
    | "transfer";
  mode: "new" | "edit";
};
type InventoryProps = { worksheet?: InventoryWorksheet };

// Заглавие на таба и етикет „назад" за всеки работен лист.
const WORKSHEET_META = {
  delivery: {
    newTitleKey: "inventoryPage.newDelivery",
    editTitleKey: "inventoryPage.editDeliveryTitle",
    backLabelKey: "inventoryPage.backToDeliveries",
  },
  writeoff: {
    newTitleKey: "inventoryPage.newWriteoff",
    editTitleKey: "inventoryPage.editWriteoffTitle",
    backLabelKey: "inventoryPage.backToWriteoffs",
  },
  stocktake: {
    newTitleKey: "inventoryPage.newStocktake",
    editTitleKey: "inventoryPage.editStocktakeTitle",
    backLabelKey: "inventoryPage.backToStocktakes",
  },
  interchangeables: {
    newTitleKey: "inventoryPage.newInterchangeTitle",
    editTitleKey: "inventoryPage.editInterchangeTitle",
    backLabelKey: "inventoryPage.backToInterchangeables",
  },
  transfer: {
    newTitleKey: "inventoryPage.newTransfer",
    editTitleKey: "inventoryPage.editTransferTitle",
    backLabelKey: "inventoryPage.backToTransfers",
  },
} as const;

const Inventory = ({ worksheet }: InventoryProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page } = useParams();

  // Свито/разгънато — помни се между посещенията (рейл на таблет по подразбиране)
  const sidebar = useSidebarCollapse("inventory");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isNarrow } = useViewport();
  const badges = useSidebarBadges();

  // Страница с незапазени промени (работен лист, разнасяне със замени…)
  // регистрира тук пазача си; ВСЯКО напускане от менюто минава през него.
  const leaveGuardRef = useRef<LeaveGuard | null>(null);
  const registerLeaveGuard = useCallback((guard: LeaveGuard | null) => {
    leaveGuardRef.current = guard;
  }, []);

  // Складът по подразбиране идва от КОНФИГУРАЦИЯТА. Ако не е зададен или вече
  // не съществува → първият наличен склад. Страниците със собствен избор на
  // склад (Наличности, Продадени) тръгват от него и могат да го сменят.
  const { data: warehouses = [] } = useWarehouses();
  const { data: settings } = useInventorySettings();
  const selectedWarehouseId = useMemo(() => {
    const def = settings?.defaultWarehouseId ?? null;
    if (def != null && warehouses.some((w) => w.id === def)) return def;
    return warehouses[0]?.id ?? null;
  }, [settings, warehouses]);
  const selectedWarehouse = useMemo(
    () => warehouses.find((w) => w.id === selectedWarehouseId) ?? null,
    [warehouses, selectedWarehouseId],
  );

  const slug = worksheet ? worksheet.page : (page ?? "");
  const currentPage = PAGES[slug] ?? null;

  // Заглавие на таба: „{име на страницата} - {име на ресторанта}".
  useEffect(() => {
    if (!currentPage) return;
    const titleKey = worksheet
      ? WORKSHEET_META[worksheet.page][
          worksheet.mode === "edit" ? "editTitleKey" : "newTitleKey"
        ]
      : currentPage.titleKey;
    document.title = `${t(titleKey)} - ${getRestaurantName()}`;
    return () => {
      document.title = `UnrealSoft - ${getRestaurantName()}`;
    };
  }, [currentPage, worksheet, t]);

  // При смяна на страница на narrow — затваряме чекмеджето
  useEffect(() => {
    if (isNarrow) setMobileOpen(false);
  }, [page, isNarrow]);

  const navigateGuarded = useCallback(
    (to: string, state?: unknown) => {
      const go = () => navigate(to, state === undefined ? undefined : { state });
      if (leaveGuardRef.current) leaveGuardRef.current(go);
      else go();
    },
    [navigate],
  );

  const shell = useMemo<InventoryShell>(
    () => ({
      isMenuButtonVisible: isNarrow,
      openMenu: () => setMobileOpen(true),
      isNarrow,
      warehouseId: selectedWarehouseId,
      warehouseName: selectedWarehouse?.name ?? null,
      registerLeaveGuard,
      navigateGuarded,
    }),
    [
      isNarrow,
      selectedWarehouseId,
      selectedWarehouse,
      registerLeaveGuard,
      navigateGuarded,
    ],
  );

  // Непознат slug → страницата по подразбиране.
  if (!currentPage) {
    return <Navigate to={`/inventory/${DEFAULT_INVENTORY_SLUG}`} replace />;
  }

  const common: CommonPageProps = {
    onOpenMobileMenu: shell.openMenu,
    isNarrow,
    warehouseId: selectedWarehouseId,
    warehouseName: selectedWarehouse?.name ?? null,
    onRegisterLeaveGuard: registerLeaveGuard,
  };

  return (
    <InventoryShellContext.Provider value={shell}>
      {/* без вграденото влачене на текст от поле в поле — виж textDragGuard */}
      <div className="flex h-screen overflow-hidden bg-canvas font-pos-sans text-ink" {...textDragGuardProps}>
        {/* Воалът зад чекмеджето на тесен екран */}
        {isNarrow && mobileOpen && (
          <div
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-scrim"
          />
        )}

        <InventorySidebar
          collapsed={sidebar.collapsed}
          onToggle={sidebar.toggle}
          activeSlug={slug}
          onNavigate={(target) => navigateGuarded(`/inventory/${target}`)}
          badges={badges}
          isNarrow={isNarrow}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          // В работния лист стрелката горе вляво връща към страницата му
          backLabel={
            worksheet ? t(WORKSHEET_META[worksheet.page].backLabelKey) : undefined
          }
          onBack={() =>
            worksheet
              ? navigateGuarded(`/inventory/${worksheet.page}`)
              : navigateGuarded("/")
          }
        />

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {worksheet?.page === "delivery" ? (
            <DeliveryWorksheetPage {...common} mode={worksheet.mode} />
          ) : worksheet?.page === "transfer" ? (
            <TransferWorksheetPage {...common} mode={worksheet.mode} />
          ) : worksheet?.page === "interchangeables" ? (
            <InterchangeableRulePage
              mode={worksheet.mode}
              onOpenMobileMenu={common.onOpenMobileMenu}
              isNarrow={common.isNarrow}
              onRegisterLeaveGuard={registerLeaveGuard}
            />
          ) : worksheet?.page === "stocktake" ? (
            <StocktakeWorksheetPage {...common} mode={worksheet.mode} />
          ) : worksheet?.page === "writeoff" ? (
            worksheet.mode === "new" ? (
              <WriteoffCreatePage {...common} />
            ) : (
              <WriteoffEditPage
                onOpenMobileMenu={common.onOpenMobileMenu}
                isNarrow={common.isNarrow}
                onRegisterLeaveGuard={registerLeaveGuard}
              />
            )
          ) : (
            currentPage.render(common)
          )}
        </main>
      </div>
    </InventoryShellContext.Provider>
  );
};

export default Inventory;
